import { ChangeEvent, FC, memo, useEffect, useMemo } from "react";
import "../Inputs.scss";
import ErrorMessage from "../../Tooltips/ErrorMessage";
import { useAppSelector } from "../../../../store";
import Roles from "../../../../models/roles";

/**
 * TextAreaInput Props Interface
 * @interface TextAreaInputProps
 */

interface TextAreaInputProps {
  /** Field name/id */
  field: string;
  /** Whether field has been touched */
  touched?: boolean;
  /** Error message if validation fails */
  error?: string;
  /** Callback for value changes */
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  /** Current input value */
  value?: string;
  /** Input label */
  label?: string;
  /** Input placeholder */
  placeholder?: string;
  /** Whether field is required */
  required?: boolean;
  /** Input ref to check the values */
  ref?: React.RefObject<HTMLInputElement | null>;
  /** Additional HTML input props */
  inputProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
  /**  */
  disabled?: boolean
}

/**
 * TextAreaInput Component
 * A reusable text area input component with support for resizing the input box,
 * error states, and accessibility features.
 */
const TextAreaInput: FC<TextAreaInputProps> = ({
  field,
  touched,
  error,
  onChange,
  value,
  label,
  placeholder,
  required = false,
  ref,
  inputProps = {},
  disabled,
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event);
  };

  useEffect(() => {
    const val = value ? value : "";
    sessionStorage.setItem(field, val);
  }, [field, value]);

  return (
    <div className="form-group">
      <textarea
        className={`form-control py-10px ${touched && error ? "is-invalid" : ""}`}
        onChange={handleChange}
        value={value}
        id={field}
        data-testid={field}
        name={field}
        placeholder={placeholder}
        {...inputProps}
        disabled={disabled}
        // rows={5}
        // style={{ resize: "none", minHeight: "80px" }}
      />

      {label && (
        <label htmlFor={field} className="form-label">
          {label}
          {required && <span className="required-asterisk">*</span>}
        </label>
      )}

      {touched && error && <ErrorMessage message={error} />}
    </div>
  );
};

export default memo(TextAreaInput);
